import {
      PublicClientApplication,
      LogLevel,
} from '@azure/msal-browser';

import { Base64 } from 'js-base64';

import {
    LoginErrors,
    classifyLoginError,
    displayName,
    selectAccount,
} from '@/plugins/authHelpers';

const config = window.Trupanion.SFAdmin.config,
    RESETPWD = config('msal.resetPwd'),
    SIGNIN = config('msal.signIn'),
    CLIENTID = config('msal.clientId'),
    TOKENSCOPE = config('msal.tokenScope'),
    PWDRESETCODE = config('msal.passwordResetRequestedCode') || 'AADB2C90118';

/**
 * localStorage key with the homeAccountId of the last successful login.
 * Used to pick the right account after a reload when the MSAL cache holds several accounts.
 */
const ACCOUNT_KEY = 'sfaAccountId';

const authorityHost = (authority) => {
    const a = document.createElement('a');
    a.href = authority || '';
    return a.host;
};


/**
 * This are the user flow login policies
 */
const Authorities = {
      resetPwd: RESETPWD, // https://devtrupstatefarmb2c.b2clogin.com/devtrupstatefarmb2c.onmicrosoft.com/B2C_1_PasswordReset',
      signIn: SIGNIN, // 'https://devtrupstatefarmb2c.b2clogin.com/tfp/devtrupstatefarmb2c.onmicrosoft.com/B2C_1_SignIn',
};

/**
 * This is the configuration for our connection to Azure B2C
 *
 * TODO we need to move clientId value to an env file
 */
const MsalConfig = {
    auth: {
        authority: Authorities.signIn,
        clientId: CLIENTID, // '5deb8c4b-bbc0-4c3d-96dc-73669e307c7d',
        knownAuthorities: [authorityHost(SIGNIN)].filter(Boolean),
        redirectUri: window.location.origin,
    },
    cache: {
        cacheLocation: 'localStorage',
        // Only needed for IE11. A cookie-backed interaction flag survives browser restarts (Edge startup boost)
        // and blocks every following login with interaction_in_progress.
        storeAuthStateInCookie: false
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error: console.error(message); break
                    case LogLevel.Info: console.info(message); break
                    case LogLevel.Verbose: console.debug(message); break
                    case LogLevel.Warning: console.warn(message); break
                };
            },
        },
    }
};

/**
 * This would be used if we were using loginPopup, for ID tokens only.
 * We are not using this as we are using a hybrid flow retrieveing
 * accessToken and idToken
 */

/**
 * We are using this request for login to retrieve application scopes.
 * This is used to get access tokens, but in conjuction with acquireTokenPopup
 * will also return idToken
 */
const TokenRequest = {
    forceRefresh: false, // Set this to "true" to skip a cached token and go to the server to get a new token
    scopes: [TOKENSCOPE], // 'https://devtrupstatefarmb2c.onmicrosoft.com/api/user.admin',
}

/**
 * This is a configured instance of our connection to Azure B2C
 */
const MsalInstance = new PublicClientApplication(MsalConfig);

/**
 * Function to logout
 */
const logout = async () => {
    localStorage.token = '';
    localStorage.nickname = '';
    localStorage.username = '';
    localStorage.removeItem(ACCOUNT_KEY);
    await MsalInstance.logout();
}

const assignLocalValues = (loginResponse) => {
    if (loginResponse && loginResponse.account) {
        localStorage.nickname = displayName(loginResponse.account);
        localStorage.username = loginResponse.account.username || '';
        localStorage.token = loginResponse.idToken ? loginResponse.idToken : '';
    }
}

/**
 * The account used for silent token requests.
 * MSAL needs a real AccountInfo here: without it the cache lookup matches tokens of every cached account.
 */
const getAccount = () => {
    let preferred = '';
    try {
        preferred = localStorage.getItem(ACCOUNT_KEY) || '';
    } catch (err) {
        preferred = '';
    }
    return selectAccount(MsalInstance.getActiveAccount(), MsalInstance.getAllAccounts(), preferred);
}

const rememberAccount = (response) => {
    if (response && response.account) {
        MsalInstance.setActiveAccount(response.account);
        localStorage.setItem(ACCOUNT_KEY, response.account.homeAccountId);
    }
}

/**
 * Removes the MSAL "interaction in progress" flag left by an interrupted popup.
 * Also removes the cookie copy written by older versions of the app (storeAuthStateInCookie: true).
 */
const resetInteractionState = () => {
    const key = `msal.${CLIENTID}.interaction.status`;
    try {
        sessionStorage.removeItem(key);
    } catch (err) {
        console.warn('Cannot clear MSAL interaction state', err);
    }
    document.cookie = `${encodeURIComponent(key)}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;`;
}

/**
 * Interactive login via popup. Returns the access token.
 * Called only from a click on "Please login": the silent attempt is already made on mount (getAccessToken),
 * and awaiting another silent call here could make the browser block the popup.
 * Rejects with the MSAL error; use classifyLoginError to handle it.
 */
const forceUserLogin = async () => {
    const response = await MsalInstance.acquireTokenPopup(TokenRequest);
    if (!response || !response.account) {
        return '';
    }
    rememberAccount(response);
    assignLocalValues(response);
    return response.accessToken || '';
}

/**
 * Starts the B2C password reset user flow in a popup.
 * Tokens from the reset policy are not used as the session, the user logs in again afterwards.
 */
const resetPassword = async () => {
    await MsalInstance.acquireTokenPopup(Object.assign({}, TokenRequest, { authority: Authorities.resetPwd }));
}

const loginErrorType = (err) => classifyLoginError(err, PWDRESETCODE);

/**
 * Attempt to authenticate via sso
 */
const ssoLogin = async () => {
    const request = Object.assign({
            loginHint: localStorage.username
        }, TokenRequest);
    const loginResponse = await MsalInstance.ssoSilent(request).catch(() => null);
    if (loginResponse !== null) {
        assignLocalValues(loginResponse);
    }
    return (loginResponse && loginResponse.account !== null);
}

/**
 * Expose the method to get a current access token, typically called before we make API calls.
 */
const getAccessToken = async () => {
    const account = getAccount();
    if (!account) {
        return '';
    }
    try {
        const request = Object.assign({}, TokenRequest, { account: account });
        const response = await MsalInstance.acquireTokenSilent(request);
        if (response.accessToken) {
            rememberAccount(response);
            return response.accessToken;
        } else {
            return '';
        }
    } catch (err) {
        console.warn('Silent token acquisition failed', err);
        return '';
    }
}

const decodeToken = (token) => {
    try {
        if (!token) {
            return;
        }

        const parts = token.split('.');
        if (!parts || parts.length !== 3) {
            return;
        }

        const payload = Base64.decode(parts[1]);
        return payload;
    } catch (err) {
        return '';
    }
}

export {
    LoginErrors,
    decodeToken,
    forceUserLogin,
    getAccessToken,
    loginErrorType,
    logout,
    resetInteractionState,
    resetPassword,
    ssoLogin,
};
